import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import { forwardRef, useImperativeHandle } from 'react';
import VirtualList from '../virtualList/index.js';
import { SelectView } from './SelectView.js';
import defaultStyles from './styles/Select.module.scss.js';
import { preventDefaultEvent, isSelectOptGroup, isSelectOption } from './utils/selectHelper.js';
import { useSelect } from './utils/useSelect.js';
import { jsx } from 'react/jsx-runtime';
import { Trigger } from '../trigger/Trigger.js';
import { omit } from '../../utils/omit.js';

const ID_SUFFIX = '_select';
const Select = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    variant = 'outlined',
    disabled,
    notFoundContent,
    trigger = 'click',
    triggerElement,
    triggerProps = {
      useAnchorWidth: true
    },
    virtualListProps,
    onPopupScroll,
    name,
    springConfig = {
      from: {
        opacity: 0,
        config: {
          duration: 150
        }
      },
      to: {
        opacity: 1,
        config: {
          duration: 150
        }
      },
      leave: {
        opacity: 0,
        config: {
          duration: 150
        }
      }
    },
    idSuffix = ID_SUFFIX,
    hasId = true,
    validationStatus
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const {
    isMultipleMode,
    value,
    inputValue,
    setPopupVisible,
    popupVisible,
    valueActive,
    setValueActive,
    isNoOptionSelected,
    childrenList,
    hasOptGroup,
    refWrapper,
    refSelectView,
    refKeyboardArrowDirection,
    getOptionInfoByValue,
    handleOptionClick,
    selectViewEventHandlers,
    hotkeyHandler,
    optionInfoMap
  } = useSelect(props);
  useImperativeHandle(ref, () => ({
    refObject: refSelectView.current?.refObject,
    focus() {
      refSelectView.current && refSelectView.current.focus();
    },
    blur() {
      refSelectView.current && refSelectView.current.blur();
    },
    hotkeyHandler,
    activeOptionValue: valueActive,
    getOptionInfoByValue,
    getOptionInfoList: () => [...optionInfoMap.values()].filter(info => info._valid)
  }), [getOptionInfoByValue, hotkeyHandler, optionInfoMap, refSelectView, valueActive]);
  const renderPopup = () => {
    const eleOptionList = childrenList.length ? /*#__PURE__*/jsx(VirtualList, {
      className: styles.list,
      ref: refWrapper,
      role: "listbox",
      data: childrenList,
      height: null,
      isStaticItemHeight: !hasOptGroup,
      itemKey: child => child.props._key,
      onMouseDown: preventDefaultEvent,
      onMouseMove: () => {
        refKeyboardArrowDirection.current = null;
      },
      onScroll: e => onPopupScroll && onPopupScroll(e.target),
      ...virtualListProps,
      threshold: virtualListProps?.threshold,
      children: child => {
        if (isSelectOptGroup(child)) {
          return /*#__PURE__*/jsx(child.type, {
            ...child.props
          });
        }
        if (isSelectOption(child)) {
          return child && /*#__PURE__*/jsx(child.type, {
            ...child.props,
            valueSelect: value,
            valueActive: valueActive,
            isMultipleMode: isMultipleMode,
            onClickOption: handleOptionClick,
            onMouseEnter: v => {
              refKeyboardArrowDirection.current === null && setValueActive(v);
            },
            onMouseLeave: () => {
              refKeyboardArrowDirection.current === null && setValueActive(undefined);
            }
          });
        }
        return child;
      }
    }) : null;
    const eleNoOptionPlaceholder = notFoundContent ? /*#__PURE__*/jsx("div", {
      className: styles.list,
      children: notFoundContent
    }) : null;
    if (!eleOptionList && !eleNoOptionPlaceholder) return null;
    return eleOptionList || eleNoOptionPlaceholder;
  };
  return /*#__PURE__*/jsx(Trigger, {
    content: renderPopup(),
    theme: {
      inner: styles.popover
    },
    trigger: trigger,
    disabled: disabled,
    position: "bottom-start",
    springConfig: springConfig,
    popupVisible: popupVisible,
    onOpen: () => setPopupVisible(true),
    onClose: () => setPopupVisible(false),
    ...omit(triggerProps, ['popupVisible', 'onVisibleChange']),
    children: triggerElement || /*#__PURE__*/jsx(SelectView, {
      ...props,
      ...selectViewEventHandlers,
      ref: refSelectView,
      validationStatus: validationStatus,
      value: value,
      variant: variant,
      inputValue: inputValue,
      popupVisible: popupVisible,
      isEmptyValue: isNoOptionSelected,
      isMultiple: isMultipleMode,
      name: name,
      idSuffix: idSuffix,
      hasId: hasId,
      optionMap: optionInfoMap
    })
  });
});
Select.displayName = 'Select';

export { Select };
